import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { environment } from './../../../../environments/environment';

@Component({
  selector: 'app-store-orders',
  templateUrl: './store-orders.component.html',
  styleUrls: ['./store-orders.component.scss']
})
export class StoreOrdersComponent implements OnInit, OnDestroy {

  // Data
  order: any[] = [];
  loading = true;
  store: any;

  // Pagination
  currentPage = 1;
  perPage = 20;
  totalOrders = 0;
  paginationArr: number[] = [];

  // Filters (server-side: id, status | client-side: date)
  filterOrderId = '';
  filterStatus = '';
  filterDate: any = null;
  isDateFiltered = false;

  // Sorting — API supports: id_order, completed_at_order, created_at_order
  sortColumn = 'id';
  sortDirection: 'asc' | 'desc' = 'desc';

  private destroy$ = new Subject<void>();
  private searchSubject$ = new Subject<void>();
  private readonly apiBase = environment.assessment_admin;

  constructor(private http: HttpClient) {}

  ngOnInit() {
    this.store = localStorage.getItem('store');

    this.searchSubject$.pipe(
      debounceTime(400),
      takeUntil(this.destroy$)
    ).subscribe(() => this.loadOrders(1));

    this.loadOrders();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data Loading ──

  loadOrders(page = this.currentPage): void {
    this.currentPage = page;
    this.loading = true;
    this.isDateFiltered = false;

    const params: string[] = [
      `page=${page}`,
      `per_page=${this.perPage}`
    ];

    // Sorting
    if (this.sortColumn === 'id') {
      params.push(`id_order=${this.sortDirection}`);
    } else if (this.sortColumn === 'date') {
      params.push(`completed_at_order=${this.sortDirection}`);
    } else if (this.sortColumn === 'created') {
      params.push(`created_at_order=${this.sortDirection}`);
    }

    // Server-side filters
    if (this.filterOrderId.trim()) {
      params.push(`id=${encodeURIComponent(this.filterOrderId.trim())}`);
    }
    if (this.filterStatus) {
      params.push(`status=${encodeURIComponent(this.filterStatus)}`);
    }

    const url = `${this.apiBase}api/v1/orders?${params.join('&')}`;

    this.http.get<any>(url)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.order = res.orders || [];
          this.totalOrders = res.meta?.total_count || this.order.length;
          this.updatePagination();
          this.loading = false;

          // Apply client-side date filter if active
          if (this.filterDate) {
            this.applyDateFilter();
          }
        },
        error: (err) => {
          this.loading = false;
          const msg = err?.error?.errors?.base?.[0] || err?.error?.errors || 'Failed to load orders.';
          Swal.fire({ icon: 'error', title: 'Oops!', text: typeof msg === 'string' ? msg : 'Something went wrong.', timer: 3000 });
        }
      });
  }

  private updatePagination(): void {
    const totalPages = Math.ceil(this.totalOrders / this.perPage);
    this.paginationArr = Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  // ── Sorting ──

  toggleSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.loadOrders(1);
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) return 'fa-sort';
    return this.sortDirection === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  // ── Filters ──

  onFilterChange(): void {
    this.searchSubject$.next();
  }

  onStatusChange(): void {
    this.loadOrders(1);
  }

  onDateChange(date: any): void {
    this.filterDate = date || null;
    if (this.filterDate) {
      this.applyDateFilter();
    } else {
      this.loadOrders(1);
    }
  }

  private applyDateFilter(): void {
    const selected = new Date(this.filterDate);
    const selectedStr = this.formatDate(selected);

    this.order = this.order.filter(row => {
      return this.formatDate(new Date(row.completed_at)) === selectedStr;
    });

    this.isDateFiltered = true;
    this.totalOrders = this.order.length;
    this.paginationArr = [];
  }

  private formatDate(d: Date): string {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }

  resetFilters(): void {
    this.filterOrderId = '';
    this.filterStatus = '';
    this.filterDate = null;
    this.isDateFiltered = false;
    this.loadOrders(1);
  }

  // ── Pagination ──

  onPerPageChange(): void {
    this.loadOrders(1);
  }

  get hasActiveFilters(): boolean {
    return !!(this.filterOrderId || this.filterStatus || this.filterDate);
  }
}
